// config/cachedAxiosConfig.js - Centralized Cached Axios Configuration
import axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';

import { API_BASE_URL } from './apiConfig';
import logger from '../services/logging/logger';
import {
  CACHE_TTL_SHORT_MS,
  CACHE_TTL_MEDIUM_MS,
  CACHE_TTL_LONG_MS
} from '../constants/timeouts';

// In development, use relative URLs so requests route through setupProxy.js (target from REACT_APP_PROXY_TARGET, default dev),
// avoiding CORS preflight failures. Absolute URLs bypass the dev proxy entirely.
const EFFECTIVE_BASE_URL = process.env.NODE_ENV === 'development' ? '/v1.0' : API_BASE_URL;

/**
 * Create a base axios instance with common configuration.
 * Attaches auth headers on every request so individual services don't have to.
 * Services that also call getAuthHeaders() and pass { headers } are harmless —
 * axios merges them and the service-provided values win due to spread order.
 */
const createBaseInstance = (baseURL = EFFECTIVE_BASE_URL) => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  instance.interceptors.request.use(async (config) => {
    try {
      const { getAuthHeaders } = await import('../services/auth/authHeaders');
      const authHeaders = getAuthHeaders();
      config.headers = { ...authHeaders, ...config.headers };
    } catch {
      // Non-blocking — let the request proceed without auth headers
    }
    return config;
  });

  return instance;
};

/**
 * Add error interceptor for handling 401/403 responses with automatic token refresh.
 *
 * AWS API Gateway + Cognito returns 403 (not 401) for expired/invalid tokens,
 * so we handle BOTH status codes. On refresh failure we dispatch `sessionExpired`
 * so the app handles cleanup in one place instead of hard-redirecting.
 */
const addErrorInterceptor = (instance) => {
  instance.interceptors.response.use(
    response => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // Skip auth handling for requests that opt out
      if (originalRequest._skipAuthRedirect) {
        return Promise.reject(error);
      }

      // Organisation suspended mid-session — force logout and show the
      // suspended-org screen, same as the pre-login check in useAuth.generateOtp
      if (error.response?.data?.errorCode === 'ORGANIZATION_SUSPENDED') {
        window.dispatchEvent(new CustomEvent('organizationSuspended', {
          detail: {
            organisationName: error.response.data.organisationName,
            suspendedAt: error.response.data.suspendedAt
          }
        }));
        return Promise.reject(error);
      }

      // Attempt token refresh on 401 or 403, but only once per request
      const shouldRefresh = (status === 401 || status === 403) && !originalRequest._retry;

      if (shouldRefresh) {
        originalRequest._retry = true;

        try {
          const { tokenService } = await import('../services/auth/tokenService');
          const newToken = await tokenService.refreshTokenIfNeeded();

          if (newToken) {
            // Inject refreshed tokens and retry
            const { getAuthHeaders } = await import('../services/auth/authHeaders');
            const freshHeaders = getAuthHeaders();
            originalRequest.headers = {
              ...originalRequest.headers,
              ...freshHeaders
            };
            return instance(originalRequest);
          }

          // No refresh token available — could be a fresh login with no refresh token stored yet,
          // or a legitimately expired session. Only dispatch sessionExpired if we actually attempted
          // a refresh (i.e. a refresh token existed) and it failed.
          const { asyncStorage } = await import('../services/storage/asyncStorageService');
          const hasRefreshToken = !!(asyncStorage.get('refreshToken') || sessionStorage.getItem('refreshToken'));
          if (hasRefreshToken) {
            throw new Error('Token refresh returned no token');
          }
          // No refresh token — propagate the original 401/403 without blowing up the session
          return Promise.reject(error);
        } catch (refreshError) {
          logger.error('Token refresh failed in interceptor', { error: refreshError.message });
          // Let the app handle logout via the sessionExpired event (single path)
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: { reason: 'token_refresh_failed' }
          }));
          return Promise.reject(refreshError);
        }
      }

      // All other errors (retried 403 = genuine permission denial, etc.) — propagate
      return Promise.reject(error);
    }
  );
};

/**
 * Setup cache on an axios instance with custom TTL
 */
const createCachedInstance = (baseURL = EFFECTIVE_BASE_URL, ttl = 60000) => {
  const baseInstance = createBaseInstance(baseURL);

  // Add error interceptor (auth is handled by getAuthHeaders() in services)
  addErrorInterceptor(baseInstance);

  // Setup cache
  const cachedInstance = setupCache(baseInstance, {
    ttl, // Default TTL in milliseconds
    methods: ['get'], // Only cache GET requests
    cachePredicate: {
      statusCheck: (status) => status >= 200 && status < 300 // Only cache successful responses
    },
    // Use memory storage for cache
    storage: undefined, // Uses default in-memory storage
    // Generate cache key based on URL and headers (exclude auth tokens from cache key)
    generateKey: (config) => {
      const url = config.url || '';
      const method = config.method || 'get';
      // Include relevant headers that affect response (tenant, user, group)
      const relevantHeaders = {
        'X-Tenant-Id': config.headers?.['X-Tenant-Id'],
        'X-User-ID': config.headers?.['X-User-ID'],
        'X-Group': config.headers?.['X-Group'],
        'X-User-Groups': config.headers?.['X-User-Groups']
      };
      return `${method}:${url}:${JSON.stringify(relevantHeaders)}`;
    },
    // Don't interpret cache control headers from server
    interpretHeader: false,
    // CRITICAL: Don't send cache headers to avoid CORS issues
    headerInterpreter: () => undefined,
    // Disable etag support to prevent If-None-Match headers
    etag: false,
    // Disable last-modified support to prevent If-Modified-Since headers
    modifiedSince: false,
    // CRITICAL: Custom request interceptor to strip cache headers before they're sent
    requestInterceptor: (config) => {
      // Remove ALL cache-related headers that cause CORS issues
      const headersToRemove = [
        'cache-control', 'Cache-Control',
        'expires', 'Expires',
        'if-modified-since', 'If-Modified-Since',
        'if-none-match', 'If-None-Match',
        'pragma', 'Pragma',
        'age', 'Age'
      ];

      if (config.headers) {
        headersToRemove.forEach(header => {
          delete config.headers[header];
        });
      }

      return config;
    }
  });

  return cachedInstance;
};

// ===== CACHED AXIOS INSTANCES WITH DIFFERENT TTL VALUES =====

// Cache TTL Constants
const CACHE_TTL_DEFAULT_MS = 60000; // 1 minute
const CACHE_TTL_USER_DATA_MS = 180000; // 3 minutes
const CACHE_TTL_INTEGRATION_MS = 120000; // 2 minutes

/**
 * Default cached instance - 1 minute TTL
 * Use for: General API calls, file listings
 */
export const cachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_DEFAULT_MS);

/**
 * Project cached instance - 5 minutes TTL
 * Use for: Project data, project files, project settings
 */
export const projectCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_MEDIUM_MS);

/**
 * Organization cached instance - 5 minutes TTL
 * Use for: Organization details, themes, settings
 */
export const organizationCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_MEDIUM_MS);

/**
 * User/Member cached instance - 3 minutes TTL
 * Use for: User lists, member lists, roles, divisions, groups
 */
export const memberCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_USER_DATA_MS);

/**
 * Static/Config cached instance - 10 minutes TTL
 * Use for: Roles, divisions, groups, configuration data
 */
export const staticCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_LONG_MS);

/**
 * Integration cached instance - 2 minutes TTL
 * Use for: Integration settings, credential status
 */
export const integrationCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_INTEGRATION_MS);

/**
 * Test Engine cached instance - 1 minute TTL
 * Use for: Test results, feature files, test settings
 */
export const testEngineCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_DEFAULT_MS);

/**
 * Token Usage cached instance - 5 minutes TTL
 * Use for: Token usage metrics, trends, user analytics
 */
export const tokenUsageCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_MEDIUM_MS);

/**
 * Short-lived cached instance - 30 seconds TTL
 * Use for: Frequently changing data like notifications
 */
export const shortLivedCachedAxios = createCachedInstance(API_BASE_URL, CACHE_TTL_SHORT_MS);

/**
 * No-cache instance - For POST/PUT/DELETE and real-time data
 * This is just a regular axios instance with error interceptor but no caching
 * Authentication is handled by getAuthHeaders() in services
 */
export const noCacheAxios = (() => {
  const instance = createBaseInstance(API_BASE_URL);
  addErrorInterceptor(instance);
  return instance;
})();

// Export default as the standard cached instance
export default cachedAxios;

/**
 * Helper function to invalidate cache for specific endpoints
 * Use after POST/PUT/DELETE operations to ensure fresh data on next GET
 */
export const invalidateCache = (instance, urlPattern) => {
  if (instance.storage) {
    // Clear all cache entries matching the URL pattern
    instance.storage.clear();
  }
};

/**
 * Helper function to clear all cache
 */
export const clearAllCache = () => {
  [
    cachedAxios,
    projectCachedAxios,
    organizationCachedAxios,
    memberCachedAxios,
    staticCachedAxios,
    integrationCachedAxios,
    testEngineCachedAxios,
    tokenUsageCachedAxios,
    shortLivedCachedAxios
  ].forEach(instance => {
    if (instance.storage) {
      instance.storage.clear();
    }
  });
};
